import { ComingSoon, type ComingSoonProps } from "@/components/ui/ComingSoon";
import { PERMISSIONS } from "@/lib/permissions";
import type { Permission } from "@/lib/permissions";

/**
 * The sections the API has not built yet.
 *
 * The admin API serves `/me`, `/admins`, `/sub-admins`, `/roles`,
 * `/permissions`, `/audit-log`, `/activity`, player accounts and moderation
 * (`/players/:id/…`, `/bans`), `/notifications` and `/announcements`. Everything else in
 * the nav is a screen this panel drew against fixtures and has no server
 * behind it, so each one routes to a `ComingSoon` page instead.
 *
 * Kept as data rather than as eighteen near-identical components, for two
 * reasons. It makes the nav's "soon" markers derivable from the same source
 * the routes use, so the two cannot disagree. And it turns "what is left to
 * build" into a list somebody can read top to bottom, which is exactly the
 * question this file exists to answer.
 *
 * Deleting an entry is how a section goes live: remove it here and point the
 * route back at the feature page, which is still sitting in `src/features`
 * where it always was.
 */
export interface UnbuiltSection extends ComingSoonProps {
  /** Route path, relative to the app root and without a leading slash. */
  path: string;
  /** Gates the route, exactly as the built sections are gated. */
  permission: Permission;
}

export const UNBUILT_SECTIONS: UnbuiltSection[] = [
  {
    path: "social",
    permission: PERMISSIONS.PLAYER_VIEW,
    eyebrow: "Live operations",
    title: "Social",
    description: "Guilds, friendships and the reports players file about each other.",
    endpoint: "/v1/admin/guilds, /v1/admin/reports",
    planned: [
      "The guild roster, and who leads each one",
      "A queue of player reports with the evidence attached",
      "Moderation outcomes, written back to the reporter",
    ],
  },
  {
    path: "characters",
    permission: PERMISSIONS.CHARACTER_VIEW,
    eyebrow: "Game configuration",
    title: "Characters",
    description: "The roster, their skins, and the stats behind each one.",
    endpoint: "/v1/admin/characters",
    planned: [
      "Every character with its base stats and abilities",
      "Skins and their unlock conditions",
      "Which characters are enabled in which game mode",
    ],
  },
  {
    path: "balancing",
    permission: PERMISSIONS.BALANCE_VIEW,
    eyebrow: "Game configuration",
    title: "Balancing",
    description: "Tuning values, and shipping them to the live game.",
    endpoint: "/v1/admin/config",
    planned: [
      "Draft, stage and publish a configuration version",
      "A diff against what is live before anything ships",
      "Rollback to a previous version, in one step",
    ],
  },
  {
    path: "missions",
    permission: PERMISSIONS.MISSION_VIEW,
    eyebrow: "Game configuration",
    title: "Missions",
    description: "Daily, weekly and event missions, and what they pay out.",
    endpoint: "/v1/admin/missions, /v1/admin/rewards",
    planned: [
      "The mission catalogue, by cadence",
      "Reward tables, and the items they can grant",
      "Completion rates per mission, to spot the ones nobody finishes",
    ],
  },
  {
    path: "progression",
    permission: PERMISSIONS.PROGRESSION_VIEW,
    eyebrow: "Game configuration",
    title: "Progression",
    description: "XP curves and what each level unlocks.",
    endpoint: "/v1/admin/progression",
    planned: [
      "The XP curve, as a curve rather than a table of numbers",
      "Level rewards, and where the gaps are",
      "Where the current player base actually sits on it",
    ],
  },
  {
    path: "matches",
    permission: PERMISSIONS.MATCH_VIEW,
    eyebrow: "Competitive",
    title: "Matches",
    description: "Match history, and the results held back for review.",
    endpoint: "/v1/admin/matches, /v1/admin/quarantine",
    planned: [
      "Recent matches with their participants and outcome",
      "A quarantine queue for results the server would not accept",
      "Resolving a quarantined match one way or the other",
    ],
  },
  {
    path: "matchmaking",
    permission: PERMISSIONS.MATCHMAKING_VIEW,
    eyebrow: "Competitive",
    title: "Matchmaking",
    description: "Queue health, and the knobs that change it.",
    endpoint: "/v1/admin/matchmaking",
    planned: [
      "Wait times and queue depth per mode and region",
      "The rating window, and how fast it widens",
      "The effect of a change before it is applied",
    ],
  },
  {
    path: "tournaments",
    permission: PERMISSIONS.TOURNAMENT_VIEW,
    eyebrow: "Competitive",
    title: "Tournaments",
    description: "Scheduled events, their brackets and their prizes.",
    endpoint: "/v1/admin/tournaments",
    planned: [
      "Create and schedule a tournament",
      "The bracket as it fills in, round by round",
      "Prize distribution, and whether it has paid out",
    ],
  },
  {
    path: "ranking",
    permission: PERMISSIONS.RANKING_VIEW,
    eyebrow: "Competitive",
    title: "Ranking",
    description: "Tiers, the rating system, and where players sit in it.",
    endpoint: "/v1/admin/ranking",
    planned: [
      "Tier thresholds and the population in each",
      "Season boundaries and what a reset does",
      "Rating distribution, to see whether the curve is where it should be",
    ],
  },
  {
    path: "leaderboards",
    permission: PERMISSIONS.LEADERBOARD_VIEW,
    eyebrow: "Competitive",
    title: "Leaderboards",
    description: "The boards players compete on, and their current standings.",
    endpoint: "/v1/admin/leaderboards",
    planned: [
      "Every board, its window and its reset schedule",
      "Current standings, with the ability to remove a fraudulent entry",
      "Season archives",
    ],
  },
  {
    path: "economy",
    permission: PERMISSIONS.ECONOMY_VIEW,
    eyebrow: "Economy",
    title: "Economy",
    description: "Currency in circulation, where it comes from and where it goes.",
    endpoint: "/v1/admin/economy",
    planned: [
      "Sources and sinks, and whether they balance",
      "A player's wallet and the ledger behind it",
      "Compensation grants, capped or uncapped depending on who is granting",
    ],
  },
  {
    path: "store",
    permission: PERMISSIONS.STORE_VIEW,
    eyebrow: "Economy",
    title: "Store",
    description: "The catalogue, the offers on it, and what sells.",
    endpoint: "/v1/admin/store, /v1/admin/purchases",
    planned: [
      "Products and their prices per region",
      "Scheduled and live offers",
      "Purchase history, and refunds",
    ],
  },
  {
    path: "analytics",
    permission: PERMISSIONS.ANALYTICS_VIEW,
    eyebrow: "Insight",
    title: "Analytics",
    description: "Retention, engagement and revenue over time.",
    endpoint: "/v1/admin/analytics",
    planned: [
      "Daily and monthly actives, and the retention curve behind them",
      "Revenue by source, cohort and region",
      "Exports, for the questions a dashboard cannot answer",
    ],
  },
  {
    path: "security",
    permission: PERMISSIONS.SECURITY_VIEW,
    eyebrow: "Insight",
    title: "Security",
    description: "Anti-cheat signals, and what was done about them.",
    endpoint: "/v1/admin/security",
    planned: [
      "Signals raised by the server, ranked by confidence",
      "The evidence behind a signal, before anyone acts on it",
      "Actions taken, and the false-positive rate they imply",
    ],
  },
];

/** Route paths of the unbuilt sections, for the nav's "soon" markers. */
export const UNBUILT_PATHS: ReadonlySet<string> = new Set(
  UNBUILT_SECTIONS.map((section) => `/${section.path}`),
);

export function UnbuiltSectionPage({ section }: { section: UnbuiltSection }) {
  return (
    <ComingSoon
      eyebrow={section.eyebrow}
      title={section.title}
      description={section.description}
      planned={section.planned}
      endpoint={section.endpoint}
    />
  );
}
